import React, { useState, useEffect } from 'react';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { SECTIONS } from '../../config/sections';
import {
  REGULAR_SERVICES,
  SPECIAL_SERVICES,
  COMBINED_SERVICE_STYLE
} from '../../config/services';
import {
  getNextSundayDate,
  checkForUpcomingSpecialService,
  getTodaySpecialService,
  shouldShowZoomButton,
  TIMEZONE
} from '../../utils/dateUtils';
import { useLanguage } from '../../i18n/LanguageContext';
import { formatTime } from '../../utils/timeFormatter';
import ServiceCard from './ServiceCard';
import '../static/ServiceInfo.css';

const ServiceInfo = () => {
  const [now, setNow] = useState(new Date());
  const nowEST = toZonedTime(now, TIMEZONE);
  const upcomingService = checkForUpcomingSpecialService(SPECIAL_SERVICES, TIMEZONE);
  const todaySpecialService = getTodaySpecialService(SPECIAL_SERVICES, TIMEZONE);
  const { t } = useLanguage();

  useEffect(() => {
    const nowEST = toZonedTime(now, TIMEZONE);
    const services = upcomingService ? [upcomingService] : REGULAR_SERVICES;
    const todayStr = formatInTimeZone(nowEST, TIMEZONE, 'yyyy-MM-dd');
    const isSunday = nowEST.getDay() === 0;

    const transitions = [];
    for (const svc of services) {
      const isRelevant = svc.date ? svc.date === todayStr : isSunday;
      if (!isRelevant) continue;

      const [sh, sm] = svc.startTime.split(':').map(Number);
      const [eh, em] = svc.endTime.split(':').map(Number);

      const windowOpen = new Date(nowEST);
      windowOpen.setHours(sh, sm - 10, 0, 0);

      const windowClose = new Date(nowEST);
      windowClose.setHours(eh, em, 0, 0);

      if (windowOpen > now) transitions.push(windowOpen);
      if (windowClose > now) transitions.push(windowClose);
    }

    if (transitions.length === 0) return;

    const next = new Date(Math.min(...transitions.map(t => t.getTime())));
    const delay = next.getTime() - now.getTime();

    const timeout = setTimeout(() => setNow(new Date()), delay);
    return () => clearTimeout(timeout);
  }, [now, upcomingService]);

  return (
    <section className="service-info-section" id={SECTIONS.SERVICE}>
      <div className="service-info-wrapper">
        <h2 className="siteHeadings">{t('services.heading')}</h2>

        <div className="service-content-grid">
          {/* Left Column - Service Times */}
          <div className="service-times-column">
            {/* Special Service Card - Only show if within a week */}
            {upcomingService && (
              <div className="service-card regular-service-card">
                <div className="concept-diagonal">
                  <div className="service-header-diagonal">
                    <h3 className="service-main-title">{t('services.nextSunday')}</h3>
                    <div className="service-date-diagonal">{getNextSundayDate(upcomingService || todaySpecialService, TIMEZONE)}</div>
                  </div>

                  <div className="service-cards-diagonal">
                    <ServiceCard
                      service={{
                        name: t(`services.specials.${upcomingService.key}`),
                        subtext: t('services.combinedSubtext'),
                        startTime: upcomingService.startTime,
                        endTime: upcomingService.endTime,
                        gradient: COMBINED_SERVICE_STYLE.gradient,
                        cardStyle: COMBINED_SERVICE_STYLE.cardStyle
                      }}
                      showZoomButton={shouldShowZoomButton(upcomingService, nowEST)}
                    />
                  </div>

                  <div className="service-note-diagonal">
                    <p className="note-intro">
                      {t('services.combinedNoteIntro')} <span className="highlight-text">{t('services.combinedNoteHighlight')}</span> {t('services.combinedNoteSuffix')}
                    </p>
                    <ul className="regular-times-list">
                      {REGULAR_SERVICES.map(svc => (
                        <li key={svc.id}>
                          <strong>{t(`services.${svc.id}.name`)}</strong>: {formatTime(svc.startTime)} – {formatTime(svc.endTime)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Regular Service Times Card - Diagonal Split Design */}
            {(!upcomingService &&
              <div className="service-card regular-service-card">
                <div className="concept-diagonal">
                  <div className="service-header-diagonal">
                    <h3 className="service-main-title">{t('services.nextSunday')}</h3>
                    <div className="service-date-diagonal">{getNextSundayDate(upcomingService || todaySpecialService, TIMEZONE)}</div>
                  </div>

                  <div className="service-cards-diagonal">
                    {REGULAR_SERVICES.map((service) => {
                      const specialServiceEndedToday = !!(todaySpecialService && !upcomingService);
                      const showZoom = !specialServiceEndedToday && shouldShowZoomButton(service, nowEST);
                      return (
                        <ServiceCard
                          key={service.id}
                          service={{ ...service, name: t(`services.${service.id}.name`) }}
                          showZoomButton={showZoom}
                        />
                      );
                    })}
                  </div>

                  <div className="service-note-diagonal">
                    {t('services.regularNote')}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Google Map */}
          <div className="map-column">
            <div className="map-container">
              <a
                href="https://maps.google.com/?q=2287+East+15th+St,+Brooklyn,+NY+11229"
                target="_blank"
                rel="noopener noreferrer"
                className="address-info"
              >
                <p className="address-line">
                  <i className="fas fa-location-dot"></i>
                  <span className="address-full">
                    {t('footer.address1')}, {t('footer.address2')}
                  </span>
                </p>
              </a>
              <div className="map-embed">
                <iframe
                  title="Brooklyn Community Christian Church Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3031.0557924787636!2d-73.96121362346147!3d40.60669227139064!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c244b1e9f1b1b1%3A0x1234567890abcdef!2s2287%20E%2015th%20St%2C%20Brooklyn%2C%20NY%2011229!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                  width="100%"
                  height="400"
                  style={{ border: 0, borderRadius: '8px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceInfo;
